import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@shared/lib/cn';
import { Icon } from '@shared/ui/icon';

import { TOAST_MESSAGE_ICON_NAME, type ToastMessageTone } from './toast-message.constant';

export interface ToastMessageAction {
  /** Figma `Action` 글자(예: "실행 취소", "닫기") */
  label: string;
  onClick: () => void;
}

export interface ToastMessageProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Figma `tone`. 기본 neutral. danger 는 `alert`, 나머지는 `status` 역할 */
  tone?: ToastMessageTone;
  /** Figma `Message` */
  children: ReactNode;
  /** Figma `action=true` + `Action` — 오른쪽 글자 버튼 */
  action?: ToastMessageAction;
  /** 앞 아이콘을 바꿔 끼운다. 없으면 tone 의 아이콘 자리(20) */
  icon?: ReactNode;
  className?: string;
}

/**
 * 잠깐 떴다 사라지는 알림 한 건의 모양(Figma `Toast`). 어두운 반전 면, 모서리 8, 여백 16×12, popover 그림자.
 * 위치 · 쌓기 · 자동 닫힘은 이 atom 이 하지 않는다 — 명령형 토스트 기반(`@shared/ui/toast` 의 `ToastHost`)이
 * `renderItem` 으로 이것을 그린다. 그래서 portal 없이 늘 제자리에 그린다(fe-rules 3절의 `inline` 과 같다).
 */
export function ToastMessage({
  tone = 'neutral',
  children,
  action,
  icon,
  className,
  ...rest
}: ToastMessageProps) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      data-tone={tone}
      {...rest}
      className={cn(
        'inline-flex items-center gap-3 rounded-lg bg-bg-inverse px-4 py-3 text-fg-inverse shadow-popover',
        className,
      )}
    >
      {icon ?? <Icon name={TOAST_MESSAGE_ICON_NAME[tone]} size="md" />}
      <p className="min-w-0 break-words typo-body-sm">{children}</p>
      {action ? (
        <button
          type="button"
          className="shrink-0 cursor-pointer typo-label whitespace-nowrap text-fg-inverse"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      ) : null}
    </div>
  );
}
